import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, CheckCircle, XCircle, FileText, Calendar, DollarSign, ShoppingCart, Store, User, Loader2, SendHorizontal, Copy, MapPin, Clock, Users, CreditCard, Ticket, ExternalLink } from "lucide-react";
import TransactionTimeline from "../components/TransactionTimeline";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import { formatDateFR, formatPriceFR, translateStatus } from "../utils/formatters";
import CopyableText, { CopyableBox } from "../components/CopyableText";
import TestimonialForm from "../components/TestimonialForm";

// Ticketing platform URLs
const PLATFORM_URLS = {
  "Ticketmaster": "https://www.ticketmaster.ca/member/my_tickets",
  "Evenko": "https://www.evenko.ca/fr/mon-compte/billets",
  "AXS": "https://www.axs.com/account/tickets",
  "DICE": "https://dice.fm/account/tickets",
  "Le Point de Vente": "https://lepointdevente.com/mon-compte",
  "Place des Arts": "https://placedesarts.com/fr/mon-compte",
  "SeatGeek": "https://seatgeek.com/account/tickets",
  "StubHub": "https://www.stubhub.com/my-account/tickets",
  "Vivid Seats": "https://www.vividseats.com/account/tickets",
  "Tickit": "https://www.tickit.ca/account",
  "JustTikit": "https://justtikit.com/account",
};

const TransactionDetailPage = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);

  // Helper function to check if payment is completed (improved detection)
  const isPaymentCompleted = (tx) => {
    if (!tx) return false;
    if (tx.payment_completed) return true;
    if (tx.payment_completed_at) return true;
    if (tx.tickets_verified) return true;
    if (tx.funds_released) return true;
    if (tx.tickets_transferred_to_buyer) return true;
    const paidStatuses = ['payment_completed', 'payment_received', 'tickets_uploaded', 'tickets_verified', 'ocr_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'];
    return paidStatuses.includes(tx.status);
  };

  // Handle transaction confirmation
  const handleConfirmTransaction = async () => {
    if (confirmingTransaction) return;
    
    setConfirmingTransaction(true);
    try {
      const response = await axios.post(`${API}/transactions/${id}/confirm`);
      
      if (response.data.success) {
        toast.success("Transfert confirmé avec succès!");
        fetchTransactionDetails(); // Refresh to get updated status
      }
    } catch (error) {
      console.error("Confirmation error:", error);
      if (error.response?.data?.already_confirmed) {
        toast.info("Vous avez déjà confirmé ce transfert");
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de la confirmation");
      }
    } finally {
      setConfirmingTransaction(false);
    }
  };

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setCheckingPaymentStatus(false);
      toast.error("Impossible de vérifier le statut du paiement. Veuillez rafraîchir la page.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setCheckingPaymentStatus(false);
        toast.success("🎉 Paiement réussi ! Le vendeur sera notifié.");
        // Refresh transaction details
        fetchTransactionDetails();
        // Clear URL params
        setSearchParams({});
        return;
      } else if (response.data.status === "expired") {
        setCheckingPaymentStatus(false);
        toast.error("La session de paiement a expiré. Veuillez réessayer.");
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setCheckingPaymentStatus(false);
      toast.error("Erreur lors de la vérification du paiement.");
    }
  }, [setSearchParams]);

  // Check for payment return from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      setCheckingPaymentStatus(true);
      toast.info("Vérification du paiement en cours...");
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info("Paiement annulé.");
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);

  useEffect(() => {
    fetchTransactionDetails();
  }, [id]);

  const fetchTransactionDetails = async () => {
    try {
      const response = await axios.get(`${API}/transactions/${id}`);
      setTransaction(response.data);
    } catch (error) {
      toast.error("Failed to load transfer details");
    }
    setLoading(false);
  };

  // Handle Stripe Connect payment (secure transfer)
  const handleStripePayment = async () => {
    if (paymentProcessing) return;
    
    setPaymentProcessing(true);
    try {
      // Try Connect payment first (with seller account)
      const response = await axios.post(`${API}/connect/payment`, {
        transaction_id: id,
        origin_url: window.location.origin
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Erreur lors de l'initialisation du paiement");
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error.response?.data?.detail || "Erreur lors du paiement";
      
      // If seller doesn't have Stripe account, show specific message
      if (errorMessage.includes("compte Stripe")) {
        toast.error(errorMessage, { duration: 6000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      created: "bg-blue-100 text-blue-800",
      pending_confirmation: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-brand-100 text-brand-800",
      payment_pending: "bg-orange-100 text-orange-800",
      payment_completed: "bg-green-100 text-green-800",
      tickets_uploaded: "bg-yellow-100 text-yellow-800",
      ocr_verified: "bg-green-100 text-green-800",
      admin_verified: "bg-green-100 text-green-800",
      completed: "bg-emerald-100 text-emerald-800",
      disputed: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatStatus = (status) => {
    return translateStatus(status);
  };

  const getUserRole = () => {
    if (!transaction) return null;
    if (transaction.buyer_email === user.email) return { type: "buyer", label: "Acheteur", icon: ShoppingCart, color: "text-blue-600" };
    if (transaction.seller_email === user.email) return { type: "seller", label: "Vendeur", icon: Store, color: "text-green-600" };
    // If email was stripped by privacy filter, check client_number
    if (transaction.buyer_client_number === user.client_number) return { type: "buyer", label: "Acheteur", icon: ShoppingCart, color: "text-blue-600" };
    if (transaction.seller_client_number === user.client_number) return { type: "seller", label: "Vendeur", icon: Store, color: "text-green-600" };
    return { type: "creator", label: "Créé par vous", icon: User, color: "text-gray-600" };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du transfert...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Transfert non trouvé</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/dashboard")}>Retour au tableau de bord</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                if (user.role === "super_admin") navigate("/super-admin");
                else if (user.role === "admin") navigate("/admin");
                else if (user.role === "owner") navigate("/business");
                else navigate("/dashboard");
              }}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-600">{user.role}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Transaction Details */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CardTitle data-testid="transaction-event-name">{transaction.event_name}</CardTitle>
                {getUserRole() && (
                  <div className={`flex items-center space-x-1 text-sm ${getUserRole().color}`}>
                    {(() => {
                      const RoleIcon = getUserRole().icon;
                      return <RoleIcon className="h-4 w-4" />;
                    })()}
                    <span className="font-medium">{getUserRole().label}</span>
                  </div>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                {formatStatus(transaction.status)}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {/* Reference Number - Prominent and Copyable */}
            <div className="mb-6 p-4 bg-gradient-to-r from-brand-50 to-blue-50 border border-brand-200 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Numéro de référence</p>
                  <p className="text-xl font-bold text-brand-700 font-mono">
                    {transaction.reference_number || transaction.id}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-brand-300 text-brand-700 hover:bg-brand-100"
                  onClick={() => {
                    navigator.clipboard.writeText(transaction.reference_number || transaction.id);
                    toast.success("Numéro de référence copié!");
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copier
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Utilisez ce numéro dans le commentaire lors de votre paiement pour faciliter la conciliation.
              </p>
            </div>

            {/* All Form Details */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Event Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Informations sur l'événement</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 text-sm">
                    <Calendar className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Date:</span>
                      <span className="ml-2" data-testid="transaction-event-date">{formatDateFR(transaction.event_date)}</span>
                    </div>
                  </div>
                  {transaction.event_time && (
                    <div className="flex items-start space-x-3 text-sm">
                      <Clock className="h-4 w-4 text-brand-600 mt-0.5" />
                      <div>
                        <span className="font-medium text-gray-600">Heure:</span>
                        <span className="ml-2">{transaction.event_time}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start space-x-3 text-sm">
                    <Store className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Plateforme:</span>
                      <span className="ml-2">{transaction.ticket_platform === 'Autre' ? transaction.custom_platform || 'Autre' : transaction.ticket_platform || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 text-sm">
                    <FileText className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Type d'événement:</span>
                      <span className="ml-2 capitalize">{transaction.event_type || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Informations sur les billets</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 text-sm">
                    <Users className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Nombre de billets:</span>
                      <span className="ml-2">{transaction.ticket_count}</span>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 text-sm">
                    <DollarSign className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Prix:</span>
                      <span className="ml-2" data-testid="transaction-price">{formatPriceFR(transaction.price)}</span>
                    </div>
                  </div>
                  
                  {/* Display ticket details grouped by ticket */}
                  {transaction.seat_type === 'siege_assigne' && transaction.ticket_details && transaction.ticket_details.length > 0 ? (
                    <div className="flex items-start space-x-3 text-sm">
                      <Ticket className="h-4 w-4 text-brand-600 mt-0.5" />
                      <div className="flex-1">
                        <span className="font-medium text-gray-600">Détails des billets:</span>
                        <div className="mt-2 space-y-1">
                          {transaction.ticket_details.map((ticket, idx) => (
                            <div key={idx} className="text-gray-800 font-medium">
                              {[
                                ticket.level && `Niveau ${ticket.level}`,
                                ticket.section && `Section ${ticket.section}`,
                                ticket.row && `Rangée ${ticket.row}`,
                                ticket.seat && `Siège ${ticket.seat}`
                              ].filter(Boolean).join(' / ') || 'Détails non spécifiés'}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : transaction.section || transaction.row || transaction.seats ? (
                    <div className="flex items-start space-x-3 text-sm">
                      <Ticket className="h-4 w-4 text-brand-600 mt-0.5" />
                      <div className="flex-1">
                        <span className="font-medium text-gray-600">Emplacement:</span>
                        <div className="mt-1 text-gray-800 font-medium">
                          {[
                            transaction.level && `Niveau ${transaction.level}`,
                            transaction.section && `Section ${transaction.section}`,
                            transaction.row && `Rangée ${transaction.row}`,
                            transaction.seats && `Siège ${transaction.seats}`
                          ].filter(Boolean).join(' / ')}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  <div className="flex items-start space-x-3 text-sm">
                    <FileText className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Type de siège:</span>
                      <span className="ml-2">
                        {transaction.seat_type === 'siege_assigne' ? 'Siège assigné' : 
                         transaction.seat_type === 'admission_generale' ? 'Admission générale' : 
                         transaction.seat_type || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {transaction.special_section_details && (
                    <div className="flex items-start space-x-3 text-sm">
                      <FileText className="h-4 w-4 text-brand-600 mt-0.5" />
                      <div>
                        <span className="font-medium text-gray-600">Détails spéciaux:</span>
                        <span className="ml-2">{transaction.special_section_details}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Parties Information - with copy functionality */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Parties impliquées</h4>
                <div className="space-y-3">
                  <div 
                    className="flex items-center space-x-3 text-sm p-2 rounded-lg hover:bg-green-50 cursor-pointer transition-colors group"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(transaction.seller_client_number);
                        toast.success("Numéro vendeur copié!");
                      } catch (err) {
                        toast.error("Impossible de copier");
                      }
                    }}
                    title="Cliquez pour copier"
                  >
                    <User className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-gray-600">Vendeur:</span>
                    <span className="font-mono font-bold text-green-700">#{transaction.seller_client_number}</span>
                    <Copy className="h-3.5 w-3.5 text-gray-300 group-hover:text-green-500 ml-auto transition-colors" />
                  </div>
                  <div 
                    className="flex items-center space-x-3 text-sm p-2 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors group"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(transaction.buyer_client_number);
                        toast.success("Numéro acheteur copié!");
                      } catch (err) {
                        toast.error("Impossible de copier");
                      }
                    }}
                    title="Cliquez pour copier"
                  >
                    <User className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-600">Acheteur:</span>
                    <span className="font-mono font-bold text-blue-700">#{transaction.buyer_client_number}</span>
                    <Copy className="h-3.5 w-3.5 text-gray-300 group-hover:text-blue-500 ml-auto transition-colors" />
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Informations de paiement</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 text-sm">
                    <CreditCard className="h-4 w-4 text-brand-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-600">Frais payés par:</span>
                      <span className="ml-2 capitalize">
                        {transaction.fee_payer === 'buyer' ? 'Acheteur' : 
                         transaction.fee_payer === 'seller' ? 'Vendeur' : 
                         transaction.fee_payer === 'split' ? 'Partagé' : 'N/A'}
                      </span>
                    </div>
                  </div>
                  {transaction.calculated_fees && (
                    <>
                      <div className="flex items-start space-x-3 text-sm">
                        <DollarSign className="h-4 w-4 text-brand-600 mt-0.5" />
                        <div>
                          <span className="font-medium text-gray-600">Frais de plateforme:</span>
                          <span className="ml-2">{formatPriceFR(transaction.calculated_fees.platform_fee || 0)}</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 text-sm">
                        <DollarSign className="h-4 w-4 text-green-600 mt-0.5" />
                        <div>
                          <span className="font-medium text-gray-600">L'acheteur paie:</span>
                          <span className="ml-2 font-semibold text-green-700">{formatPriceFR(transaction.calculated_fees.buyer_pays || transaction.total_amount || 0)}</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 text-sm">
                        <DollarSign className="h-4 w-4 text-blue-600 mt-0.5" />
                        <div>
                          <span className="font-medium text-gray-600">Le vendeur reçoit:</span>
                          <span className="ml-2 font-semibold text-blue-700">{formatPriceFR(transaction.calculated_fees.seller_receives || 0)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Suivi du transfert</CardTitle>
            <CardDescription>Progression étape par étape</CardDescription>
          </CardHeader>
          <CardContent>
            <TransactionTimeline 
              transaction={transaction} 
              currentUserEmail={user?.email}
              user={user}
              onRatingSubmitted={fetchTransactionDetails}
              onPaymentClick={() => {
                // Scroll to payment section
                const paymentSection = document.getElementById('payment-section');
                if (paymentSection) {
                  paymentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              onConfirmClick={handleConfirmTransaction}
              confirmingTransaction={confirmingTransaction}
            />
            
            {/* Testimonial Prompt - Show when transaction is completed */}
            {transaction.status === 'completed' && (
              <div className="mt-6 p-5 bg-gradient-to-r from-brand-50 to-green-50 rounded-lg border border-brand-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-brand-100 rounded-full">
                      <CheckCircle className="h-6 w-6 text-brand-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-brand-900">Transfert réussi !</h4>
                      <p className="text-sm text-brand-700">Partagez votre expérience et aidez d'autres utilisateurs</p>
                    </div>
                  </div>
                  <TestimonialForm 
                    user={user}
                    transactionId={transaction.id || id}
                    triggerText="Laissez un témoignage"
                    triggerVariant="default"
                    triggerClassName="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ACTION REQUISE - Call to Action Section at Top */}
        {(() => {
          const isBuyer = user?.email === transaction.buyer_email || user?.client_number === transaction.buyer_client_number;
          const isSeller = user?.email === transaction.seller_email || user?.client_number === transaction.seller_client_number;
          const bothConfirmed = transaction.buyer_confirmed && transaction.seller_confirmed;
          
          // Seller CTA: Transfer tickets (visible once confirmed, before tickets verified)
          if (isSeller && transaction.seller_confirmed && 
              !['tickets_uploaded', 'tickets_verified', 'ocr_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'].includes(transaction.status) &&
              !transaction.tickets_verified) {
            return (
              <Card className="mb-6 border-2 border-brand-400 bg-gradient-to-r from-brand-50 to-indigo-50 shadow-lg">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-brand-100 rounded-full">
                        <SendHorizontal className="h-6 w-6 text-brand-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-brand-900">Action requise : Transférez vos billets</h3>
                        <p className="text-sm text-brand-700">Envoyez vos billets via votre plateforme de billetterie pour compléter le transfert</p>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      className="bg-brand-600 hover:bg-brand-700 text-white shadow-md"
                      onClick={() => {
                        const platformUrl = PLATFORM_URLS[transaction.ticket_platform];
                        if (platformUrl) {
                          window.open(platformUrl, '_blank', 'noopener,noreferrer');
                        } else {
                          // Scroll to instructions if platform not recognized
                          const ticketSection = document.getElementById('ticket-transfer-section');
                          if (ticketSection) {
                            ticketSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          toast.info("Connectez-vous à votre plateforme de billetterie pour transférer vos billets");
                        }
                      }}
                    >
                      <ExternalLink className="h-5 w-5 mr-2" />
                      Ouvrir {transaction.ticket_platform || 'la billetterie'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          // Buyer CTA: Pay now (visible once both confirmed and payment not done)
          if (isBuyer && bothConfirmed && !isPaymentCompleted(transaction)) {
            return (
              <Card className="mb-6 border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 rounded-full">
                        <DollarSign className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-green-900">Action requise : Effectuez votre paiement</h3>
                        <p className="text-sm text-green-700">Payez pour sécuriser vos billets dès maintenant</p>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white shadow-md"
                      onClick={() => {
                        const paymentSection = document.getElementById('payment-section');
                        if (paymentSection) {
                          paymentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                    >
                      <DollarSign className="h-5 w-5 mr-2" />
                      Payer maintenant
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          return null;
        })()}

        {/* Confirmation Section - Show if user hasn't confirmed yet */}
        {transaction.status === "pending_confirmation" && (
          (() => {
            const isBuyer = user?.email === transaction.buyer_email || user?.client_number === transaction.buyer_client_number;
            const isSeller = user?.email === transaction.seller_email || user?.client_number === transaction.seller_client_number;
            const userHasConfirmed = (isBuyer && transaction.buyer_confirmed) || (isSeller && transaction.seller_confirmed);
            const otherPartyConfirmed = (isBuyer && transaction.seller_confirmed) || (isSeller && transaction.buyer_confirmed);
            
            if (!userHasConfirmed && (isBuyer || isSeller)) {
              return (
                <Card className="mb-6 border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-900">
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Confirmation requise
                    </CardTitle>
                    <CardDescription className="text-orange-700">
                      Veuillez confirmer les détails de ce transfert
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Transaction Summary */}
                    <div className="bg-white border border-orange-200 rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold text-gray-900">Résumé du transfert</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Événement:</span>
                          <p className="font-medium">{transaction.event_name}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Date:</span>
                          <p className="font-medium">{transaction.event_date}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Nombre de billets:</span>
                          <p className="font-medium">{transaction.ticket_count}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Prix:</span>
                          <p className="font-medium">{formatPriceFR(transaction.price)}</p>
                        </div>
                        {transaction.calculated_fees && (
                          <>
                            <div>
                              <span className="text-gray-600">Total des frais:</span>
                              <p className="font-medium">{formatPriceFR(transaction.calculated_fees.total)}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">
                                {isBuyer ? "Vos frais:" : "Frais vendeur:"}
                              </span>
                              <p className="font-medium">
                                {isBuyer 
                                  ? formatPriceFR(transaction.calculated_fees.buyer_share || transaction.calculated_fees.total)
                                  : formatPriceFR(transaction.calculated_fees.seller_share || '0.00')
                                }
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* What user will pay/receive */}
                      <div className="mt-4 pt-3 border-t border-orange-200">
                        {isBuyer ? (
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-gray-900">Total à payer:</span>
                            <span className="text-xl font-bold text-orange-700">
                              {formatPriceFR(transaction.total_amount || (parseFloat(transaction.price) + parseFloat(transaction.calculated_fees?.buyer_share || transaction.calculated_fees?.total || 0)))}
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-gray-900">Vous recevrez:</span>
                            <span className="text-xl font-bold text-brand-700">
                              {formatPriceFR(transaction.seller_receives || (parseFloat(transaction.price) - parseFloat(transaction.calculated_fees?.seller_share || 0)))}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Other party status */}
                    {otherPartyConfirmed && (
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          {isBuyer ? "Le vendeur" : "L'acheteur"} a déjà confirmé ce transfert.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Confirmation Button */}
                    <Button
                      onClick={handleConfirmTransaction}
                      disabled={confirmingTransaction}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="lg"
                    >
                      {confirmingTransaction ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Confirmation en cours...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-5 w-5" />
                          Je confirme ce transfert
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-orange-700 text-center">
                      En confirmant, vous acceptez les termes de ce transfert.
                    </p>
                  </CardContent>
                </Card>
              );
            }
            
            // User has confirmed but waiting for other party
            if (userHasConfirmed && !otherPartyConfirmed) {
              return (
                <Card className="mb-6 border-blue-200 bg-blue-50">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <p className="text-blue-800 font-medium">
                        Vous avez confirmé. En attente de la confirmation de {isBuyer ? "le vendeur" : "l'acheteur"}...
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            
            return null;
          })()
        )}

        {/* Transfer Tickets Section */}
        {(transaction.status === "created" || transaction.status === "confirmed" || transaction.status === "pending_confirmation") && getUserRole()?.type === "seller" && (
          <Card id="ticket-transfer-section" className="mb-6 border-brand-200 bg-brand-50">
            <CardHeader>
              <CardTitle className="flex items-center text-brand-900">
                <SendHorizontal className="mr-2 h-5 w-5" />
                Transfert des billets
              </CardTitle>
              <CardDescription className="text-brand-700">
                Transférez vos billets via votre plateforme de billetterie vers le compte Middleman
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Box */}
              <div className="bg-white border-2 border-brand-300 rounded-lg p-4">
                <h4 className="font-bold text-brand-900 mb-3 flex items-center gap-2">
                  <Ticket className="h-5 w-5" />
                  Billets à transférer
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Événement:</span>
                    <p className="font-semibold text-gray-900">{transaction.event_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <p className="font-semibold text-gray-900">{transaction.event_date}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Nombre de billets:</span>
                    <p className="font-semibold text-gray-900">{transaction.ticket_count} billet{transaction.ticket_count > 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Plateforme:</span>
                    <p className="font-semibold text-gray-900">{transaction.ticket_platform || 'Non spécifiée'}</p>
                  </div>
                </div>
                {transaction.seat_type === 'siege_assigne' && transaction.ticket_details && transaction.ticket_details.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-gray-600 text-sm font-medium">Détails des billets à transférer:</span>
                    <div className="mt-2 space-y-1">
                      {transaction.ticket_details.map((ticket, idx) => (
                        <div key={idx} className="text-gray-800 font-medium">
                          {[
                            ticket.level && `Niveau ${ticket.level}`,
                            ticket.section && `Section ${ticket.section}`,
                            ticket.row && `Rangée ${ticket.row}`,
                            ticket.seat && `Siège ${ticket.seat}`
                          ].filter(Boolean).join(' / ') || 'Détails non spécifiés'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Alert className="bg-white border-brand-300">
                <AlertDescription>
                  <div className="space-y-3">
                    <p className="font-semibold text-gray-900">
                      📧 Adresse courriel pour le transfert :
                    </p>
                    <div className="flex items-center justify-between bg-brand-100 border border-brand-300 rounded-lg p-3">
                      <code className="text-lg font-mono font-bold text-brand-800">
                        info@transfertdebillets.ca
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText('info@transfertdebillets.ca');
                          toast.success('Email copié dans le presse-papiers!');
                        }}
                        className="ml-3"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Copier
                      </Button>
                    </div>
                    
                    {/* Platform-specific instructions */}
                    <div className="mt-4 space-y-2 text-sm text-gray-700">
                      <p className="font-bold text-gray-900">📋 Instructions pour {transaction.ticket_platform || 'votre plateforme'} :</p>
                      {transaction.ticket_platform === 'Ticketmaster' ? (
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                          <li>Connectez-vous à votre compte <strong>Ticketmaster</strong></li>
                          <li>Allez dans <strong>"Mes billets"</strong></li>
                          <li>Sélectionnez les billets pour <strong>{transaction.event_name}</strong></li>
                          <li>Cliquez sur <strong>"Transférer"</strong></li>
                          <li>Entrez l'adresse: <strong>info@transfertdebillets.ca</strong></li>
                          <li>Confirmez le transfert</li>
                        </ol>
                      ) : transaction.ticket_platform === 'Evenko' ? (
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                          <li>Connectez-vous à votre compte <strong>Evenko</strong></li>
                          <li>Allez dans <strong>"Mes billets"</strong></li>
                          <li>Sélectionnez les billets pour <strong>{transaction.event_name}</strong></li>
                          <li>Cliquez sur <strong>"Transférer"</strong></li>
                          <li>Entrez l'adresse: <strong>info@transfertdebillets.ca</strong></li>
                          <li>Confirmez le transfert</li>
                        </ol>
                      ) : transaction.ticket_platform === 'AXS' ? (
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                          <li>Ouvrez l'application ou le site <strong>AXS</strong></li>
                          <li>Allez dans <strong>"Your Tickets"</strong></li>
                          <li>Sélectionnez les billets pour <strong>{transaction.event_name}</strong></li>
                          <li>Cliquez sur <strong>"Transfer"</strong></li>
                          <li>Entrez l'adresse: <strong>info@transfertdebillets.ca</strong></li>
                          <li>Confirmez le transfert</li>
                        </ol>
                      ) : (
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                          <li>Connectez-vous à votre compte de billetterie</li>
                          <li>Accédez à <strong>"Mes billets"</strong></li>
                          <li>Sélectionnez les billets pour <strong>{transaction.event_name}</strong></li>
                          <li>Cliquez sur <strong>"Transférer"</strong></li>
                          <li>Entrez l'adresse: <strong>info@transfertdebillets.ca</strong></li>
                          <li>Confirmez le transfert</li>
                        </ol>
                      )}
                    </div>

                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">💡 Pourquoi ce processus?</span> En transférant vos billets directement dans notre compte de vérification, nous pouvons garantir leur authenticité. Une fois les billets reçus et vérifiés, nous procédons au paiement et transférons les billets à l'acheteur.
                      </p>
                    </div>

                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3">
                      <p className="text-xs text-amber-800">
                        <span className="font-semibold">⏱️ Délai :</span> Notre équipe vérifiera les billets dans les 24h suivant le transfert et vous recevrez une notification.
                      </p>
                    </div>

                    {/* Quick access button to platform */}
                    {PLATFORM_URLS[transaction.ticket_platform] && (
                      <div className="mt-4 pt-4 border-t border-brand-200">
                        <Button
                          size="lg"
                          className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                          onClick={() => window.open(PLATFORM_URLS[transaction.ticket_platform], '_blank', 'noopener,noreferrer')}
                        >
                          <ExternalLink className="h-5 w-5 mr-2" />
                          Ouvrir {transaction.ticket_platform} pour transférer
                        </Button>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Payment Section - Buyer only, after both confirmed */}
        {transaction.status === "confirmed" && getUserRole()?.type === "buyer" && !isPaymentCompleted(transaction) && (
          <Card id="payment-section" className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center text-green-900">
                <DollarSign className="mr-2 h-5 w-5" />
                Paiement du transfert
              </CardTitle>
              <CardDescription className="text-green-700">
                Effectuez votre paiement pour sécuriser vos billets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Box - Always visible */}
              <div className="bg-white border-2 border-green-300 rounded-lg p-4">
                <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                  <Ticket className="h-5 w-5" />
                  Résumé de votre achat
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Événement:</span>
                    <span className="font-semibold text-gray-900">{transaction.event_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-semibold text-gray-900">{transaction.event_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nombre de billets:</span>
                    <span className="font-semibold text-gray-900">{transaction.ticket_count} billet{transaction.ticket_count > 1 ? 's' : ''}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prix des billets:</span>
                      <span className="font-semibold">{formatPriceFR(transaction.price)}</span>
                    </div>
                    {transaction.calculated_fees && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frais de service:</span>
                        <span className="font-semibold">{formatPriceFR(transaction.calculated_fees.buyer_share || transaction.calculated_fees.total)}</span>
                      </div>
                    )}
                    <div className="flex justify-between mt-2 pt-2 border-t border-green-200">
                      <span className="text-lg font-bold text-green-800">Total à payer:</span>
                      <span className="text-xl font-bold text-green-800">
                        {formatPriceFR(transaction.total_amount || (parseFloat(transaction.price) + parseFloat(transaction.calculated_fees?.buyer_share || transaction.calculated_fees?.total || 0)))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Selector */}
              <PaymentMethodSelector
                transaction={transaction}
                user={user}
                totalAmount={transaction.total_amount || (parseFloat(transaction.price) + parseFloat(transaction.calculated_fees?.buyer_share || transaction.calculated_fees?.total || 0))}
                onPaymentMethodSelected={(method) => {
                  console.log("Payment method selected:", method);
                }}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TransactionDetailPage;